import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Loader2, Search, Filter, UserPlus, Users, X, Phone, MapPin, Calendar } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../ui/table';
import { Badge } from '../ui/badge';
import { useApiRequest } from '../../hooks/useApiRequest';
import { API_ENDPOINTS } from '../../lib/api-endpoints';
import { PaginationWrapper } from '../ui/PaginationWrapper';

interface FreeUser {
  _id: string;
  name: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  country: string;
  gender: string;
  currentPlan: string;
  registeredDate: string;
  isAssigned: boolean;
  assignmentCount: number;
}

interface MarketingUser {
  _id: string;
  name: string;
  email: string;
  assignedLeadsCount: number;
}

export default function AdminAssignLeads() {
  const [freeUsers, setFreeUsers] = useState<FreeUser[]>([]);
  const [marketingUsers, setMarketingUsers] = useState<MarketingUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [genderFilter, setGenderFilter] = useState<string>('all');
  const [showAssignedFilter, setShowAssignedFilter] = useState<'all' | 'unassigned' | 'assigned'>('unassigned');
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isBulkAssignDialogOpen, setIsBulkAssignDialogOpen] = useState(false);
  const [selectedMarketingUser, setSelectedMarketingUser] = useState<string>('');
  const [selectedMarketingUsers, setSelectedMarketingUsers] = useState<string[]>([]);
  const { loading, execute } = useApiRequest();
  const { loading: assigning, execute: executeAssign } = useApiRequest();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Get unique cities for filter
  const cities = useMemo(() => {
    const uniqueCities = Array.from(new Set(freeUsers.map(user => user.city)));
    return uniqueCities.sort();
  }, [freeUsers]);

  // Filter users based on search, city, gender, and assignment status
  const filteredUsers = useMemo(() => {
    return freeUsers.filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.phone.includes(searchTerm) ||
                           user.city.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCity = cityFilter === 'all' || user.city === cityFilter;
      const matchesGender = genderFilter === 'all' || user.gender?.toLowerCase() === genderFilter.toLowerCase();
      const matchesAssignment = showAssignedFilter === 'all' || 
                               (showAssignedFilter === 'unassigned' && !user.isAssigned) ||
                               (showAssignedFilter === 'assigned' && user.isAssigned);
      return matchesSearch && matchesCity && matchesGender && matchesAssignment;
    });
  }, [freeUsers, searchTerm, cityFilter, genderFilter, showAssignedFilter]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    console.log('🔄 Fetching assign leads data...');

    // Try to fetch from leads endpoint first
    try {
      const [usersRes, marketingRes] = await Promise.all([
        execute('/leads/admin/free-users'),
        execute('/leads/admin/marketing-users')
      ]);

      if (usersRes.success && usersRes.data) {
        console.log('✅ Leads endpoint response:', {
          freeUsers: usersRes.data.data?.length || 0,
          total: usersRes.data.total,
          unassigned: usersRes.data.unassigned
        });
        setFreeUsers(usersRes.data.data || usersRes.data);
      }

      if (marketingRes.success && marketingRes.data) {
        console.log('👥 Setting marketing users:', marketingRes.data.data?.length || 0);
        setMarketingUsers(marketingRes.data.data || marketingRes.data);
      }
    } catch (leadsError: any) {
      // Fallback: Fetch all users and filter
      console.warn('⚠️ Leads endpoint error, using fallback');
      
      const allUsersRes = await execute(API_ENDPOINTS.ADMIN.GET_USERS);

      if (allUsersRes.success && allUsersRes.data) {
        const allUsers = allUsersRes.data.users || allUsersRes.data || [];
        
        // Filter free users
        const freeUsersList = allUsers
          .filter((u: any) => u.role === 'user' && (!u.currentPlan || u.currentPlan.toLowerCase() === 'free'))
          .map((u: any) => ({
            _id: u._id,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown',
            phone: u.phone || 'N/A',
            email: u.email || 'N/A',
            city: u.city || 'Unknown',
            state: u.state || 'Unknown',
            country: u.country || 'Unknown',
            gender: u.gender || 'Unknown',
            currentPlan: u.currentPlan || 'Free',
            registeredDate: u.createdAt || new Date().toISOString(),
            isAssigned: u.isAssignedToMarketing || false,
            assignmentCount: u.assignmentCount || 0
          }));

        setFreeUsers(freeUsersList);

        // Filter marketing users
        const marketingUsersList = allUsers
          .filter((u: any) => u.role === 'marketing')
          .map((u: any) => ({
            _id: u._id,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown',
            email: u.email || 'N/A',
            assignedLeadsCount: u.assignedLeadsCount || 0
          }));

        setMarketingUsers(marketingUsersList);
      }
    }
  };

  const handleSelectAll = () => {
    const unassignedFilteredUsers = filteredUsers.filter(user => !user.isAssigned);
    if (selectedUsers.length === unassignedFilteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(unassignedFilteredUsers.map(user => user._id));
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSingleAssign = () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    setIsAssignDialogOpen(true);
  };

  const handleBulkAssign = () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to assign');
      return;
    }
    setIsBulkAssignDialogOpen(true);
  };

  const confirmSingleAssign = async () => {
    if (!selectedMarketingUser) {
      toast.error('Please select a marketing user');
      return;
    }

    const response = await executeAssign(API_ENDPOINTS.ADMIN.ASSIGN_LEAD, {
      method: 'POST',
      data: {
        userIds: selectedUsers,
        marketingUserIds: [selectedMarketingUser],
        type: 'single'
      }
    });

    if (response.success) {
      toast.success(response.message || 'Leads assigned successfully');
      setSelectedUsers([]);
      setSelectedMarketingUser('');
      setIsAssignDialogOpen(false);
      fetchData(); // Refresh data
    } else {
      toast.error(response.error || 'Failed to assign leads');
    }
  };

  const confirmBulkAssign = async () => {
    if (selectedMarketingUsers.length === 0) {
      toast.error('Please select at least one marketing user');
      return;
    }

    const response = await executeAssign(API_ENDPOINTS.ADMIN.ASSIGN_LEAD, {
      method: 'POST',
      data: {
        userIds: selectedUsers,
        marketingUserIds: selectedMarketingUsers,
        type: 'bulk'
      }
    });

    if (response.success) {
      toast.success(response.message || 'Leads assigned successfully');
      setSelectedUsers([]);
      setSelectedMarketingUsers([]);
      setIsBulkAssignDialogOpen(false);
      fetchData(); // Refresh data
    } else {
      toast.error(response.error || 'Failed to assign leads');
    }
  };

  const handleMarketingUserToggle = (userId: string) => {
    setSelectedMarketingUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const unassignedCount = freeUsers.filter(u => !u.isAssigned).length;
  const assignedCount = freeUsers.filter(u => u.isAssigned).length;

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-pink-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading leads data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-gray-900 mb-2">Assign Leads to Marketing Users</h1>
        <p className="text-gray-600">Manage and assign free user leads to marketing team members</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Free Users</CardDescription>
            <CardTitle className="text-3xl">{freeUsers.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Unassigned Leads</CardDescription>
            <CardTitle className="text-3xl text-orange-600">{unassignedCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Assigned Leads</CardDescription>
            <CardTitle className="text-3xl text-green-600">{assignedCount}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by name, phone, or city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* City Filter */}
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by city" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map(city => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Gender Filter */}
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Genders</SelectItem>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>

            {/* Assignment Status Filter */}
            <Select value={showAssignedFilter} onValueChange={(value: any) => setShowAssignedFilter(value)}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="unassigned">Unassigned Only</SelectItem>
                <SelectItem value="assigned">Assigned Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <Button
              onClick={handleSingleAssign}
              disabled={selectedUsers.length === 0}
              className="flex items-center"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Assign to One User ({selectedUsers.length} selected)
            </Button>
            <Button
              onClick={handleBulkAssign}
              disabled={selectedUsers.length === 0}
              variant="outline"
              className="flex items-center"
            >
              <Users className="h-4 w-4 mr-2" />
              Bulk Assign ({selectedUsers.length} selected)
            </Button>
            {selectedUsers.length > 0 && (
              <Button
                onClick={() => setSelectedUsers([])}
                variant="ghost"
                className="flex items-center"
              >
                <X className="h-4 w-4 mr-2" />
                Clear Selection
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Free Users List</CardTitle>
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all"
                checked={selectedUsers.length === filteredUsers.filter(u => !u.isAssigned).length && filteredUsers.filter(u => !u.isAssigned).length > 0}
                onCheckedChange={handleSelectAll}
              />
              <Label htmlFor="select-all" className="cursor-pointer">
                Select All Unassigned
              </Label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Gender</TableHead>
                  <TableHead>Current Plan</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Registered Date</TableHead>
                  <TableHead>Assigned Count</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                      No users found matching your criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => (
                    <TableRow key={user._id} className={selectedUsers.includes(user._id) ? 'bg-blue-50' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={selectedUsers.includes(user._id)}
                          onCheckedChange={() => handleSelectUser(user._id)}
                          disabled={user.isAssigned}
                        />
                      </TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-2 text-gray-400" />
                          {user.phone}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          user.gender?.toLowerCase() === 'male' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          user.gender?.toLowerCase() === 'female' ? 'bg-pink-50 text-pink-700 border-pink-200' :
                          'bg-gray-50 text-gray-700 border-gray-200'
                        }>
                          {user.gender || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          user.currentPlan === 'Free' ? 'bg-gray-50 text-gray-700 border-gray-200' :
                          user.currentPlan === 'Basic' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          user.currentPlan === 'Premium' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                          user.currentPlan === 'Gold' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                          'bg-green-50 text-green-700 border-green-200'
                        }>
                          {user.currentPlan || 'Free'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                          {user.city}, {user.state}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                          {new Date(user.registeredDate).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          user.assignmentCount === 0 ? 'bg-green-50 text-green-700 border-green-200' :
                          user.assignmentCount === 1 ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          user.assignmentCount <= 3 ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                          'bg-red-50 text-red-700 border-red-200'
                        }>
                          {user.assignmentCount || 0}x
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.isAssigned ? (
                          <Badge variant="secondary">Assigned</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Available
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Single Assignment Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Leads to Marketing User</DialogTitle>
            <DialogDescription>
              Select a marketing user to assign {selectedUsers.length} lead(s)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="marketing-user">Marketing User</Label>
            <Select value={selectedMarketingUser} onValueChange={setSelectedMarketingUser}>
              <SelectTrigger id="marketing-user" className="mt-2">
                <SelectValue placeholder="Select a marketing user" />
              </SelectTrigger>
              <SelectContent>
                {marketingUsers.map(user => (
                  <SelectItem key={user._id} value={user._id}>
                    {user.name} ({user.assignedLeadsCount} current leads)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)} disabled={assigning}>
              Cancel
            </Button>
            <Button onClick={confirmSingleAssign} disabled={assigning}>
              {assigning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Assigning...
                </>
              ) : (
                'Assign Leads'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assignment Dialog */}
      <Dialog open={isBulkAssignDialogOpen} onOpenChange={setIsBulkAssignDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Assign Leads</DialogTitle>
            <DialogDescription>
              Distribute {selectedUsers.length} lead(s) among selected marketing users
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="mb-3 block">Select Marketing Users</Label>
            <div className="border rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
              {marketingUsers.map(user => (
                <div key={user._id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                  <Checkbox
                    id={`marketing-${user._id}`}
                    checked={selectedMarketingUsers.includes(user._id)}
                    onCheckedChange={() => handleMarketingUserToggle(user._id)}
                  />
                  <Label htmlFor={`marketing-${user._id}`} className="flex-1 cursor-pointer">
                    <div>{user.name}</div>
                    <div className="text-sm text-gray-500">{user.email} • {user.assignedLeadsCount} current leads</div>
                  </Label>
                </div>
              ))}
            </div>
            {selectedMarketingUsers.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Distribution:</strong> {selectedUsers.length} leads ÷ {selectedMarketingUsers.length} users = ~{Math.floor(selectedUsers.length / selectedMarketingUsers.length)} leads per user
                  {selectedUsers.length % selectedMarketingUsers.length > 0 && (
                    <span> (+{selectedUsers.length % selectedMarketingUsers.length} extra to first users)</span>
                  )}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkAssignDialogOpen(false)} disabled={assigning}>
              Cancel
            </Button>
            <Button onClick={confirmBulkAssign} disabled={selectedMarketingUsers.length === 0 || assigning}>
              {assigning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Assigning...
                </>
              ) : (
                'Assign Leads'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      <PaginationWrapper
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={setItemsPerPage}
      />
    </div>
  );
}